import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';

import { Observable, of, map } from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { NativeBridgeService } from '@fcom/core/services/native-bridge/native-bridge.service';
import { casProfileName, casProfileOrNot, loginStatus } from '@fcom/core/selectors/login.selector';
import { CasCustomerType, CasProfile, LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { finShare } from '@fcom/rx/operators/finShare';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { IconSet } from '@fcom/ui-components/components/icons/icons.interfaces';

import { ElementTypes } from '../../interfaces/gtm.interface';
import { MediaQueryService } from '../../services/media-query/media-query.service';

const SHOW_BUTTON_STATES = [
  LoginStatus.LOGGED_IN,
  LoginStatus.NOT_LOGGED_IN,
  LoginStatus.ERROR,
  LoginStatus.PENDING,
  LoginStatus.INITIAL,
];

@Component({
  selector: 'fin-login-button',
  templateUrl: 'login-button.component.html',
  styleUrls: ['./login-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginButtonComponent implements OnInit {
  private store$ = inject<Store<AppState>>(Store);
  private nativeBridgeService = inject(NativeBridgeService);
  private mediaQueryService = inject(MediaQueryService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly IconPosition = IconPosition;
  readonly LoginStatus = LoginStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconLibrary = IconLibrary;

  showButton$: Observable<boolean>;
  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');
  icon$: Observable<IconSet>;

  profileName$: Observable<string>;
  loginStatus$: Observable<LoginStatus>;

  @Input()
  showName = false;

  @Input()
  nameStyle = 'font-body-medium';

  @Input()
  trackingContext: string;

  ngOnInit(): void {
    this.profileName$ = this.store$.pipe(casProfileName());
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.icon$ = this.store$.pipe(
      casProfileOrNot(),
      map((casProfile) => this.getIcon(casProfile))
    );
    this.showButton$ = this.nativeBridgeService.isInsideNativeWebView
      ? of(false)
      : this.loginStatus$.pipe(
          map((s) => SHOW_BUTTON_STATES.indexOf(s) !== -1),
          finShare()
        );
  }

  private getIcon(casProfile: CasProfile | undefined): IconSet {
    const isCorporate = casProfile?.customerType === CasCustomerType.CORPORATE_USER;

    const name = !casProfile ? SvgLibraryIcon.SINGLE_PAX : isCorporate ? SvgLibraryIcon.B2B : SvgLibraryIcon.PROFILE;

    return { category: IconLibrary.SVG_LIBRARY, name };
  }
}
